import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
  useQuery
} from '@apollo/client';

interface IAutoCompleteSearchingFieldProps {
  id?: string;
  label: string;
  query?: {
    query: DocumentNode | TypedDocumentNode<OperationVariables>;
    getter: string;
  };
  options?: Array<Record<string, string | number>>;
  optionLabelKey: string;
  value?: Record<string, string | number>;
  defaultValue?: Record<string, string | number>;
  onChange: (newValue: Record<string, string | number> | null) => void;
  searchingValue?: (name: string) => void;
  touched?: boolean;
  errors?: string;
  disabled?: boolean;
  projectMembers?: Array<Record<string, string | number>>;
}

const AutoCompleteSearchComponent = ({
  id,
  label,
  query,
  options,
  optionLabelKey,
  defaultValue,
  value,
  onChange,
  touched,
  errors,
  disabled,
  projectMembers
}: IAutoCompleteSearchingFieldProps): JSX.Element => {
  const [searchInput, setSearchInput] = useState<string>(
    value ? value[optionLabelKey]?.toString() || '' : ''
  );
  const [searchResults, setSearchResults] = useState<
    Array<Record<string, string | number>>
  >(options ?? []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, refetch, loading } = useQuery(query?.query ?? ({} as any), {
    variables: { displayName: searchInput },
    skip: !query || !searchInput
  });

  useEffect(() => {
    if (query && searchInput) {
      void refetch({ displayName: searchInput });
    }
  }, [searchInput, query, refetch]);

  useEffect(() => {
    if (data) {
      setSearchResults(data[query!.getter].data);
    } else if (options && !searchInput) {
      setSearchResults(options);
    }
  }, [data, options, query, searchInput]);

  const handleSearchingName = (event: string): void => {
    setSearchInput(event);
    if (event === '') {
      onChange(defaultValue as Record<string, string | number>);
      if (options) {
        setSearchResults(options);
      }
    }
  };

  const handleInputChange = (
    _event: React.SyntheticEvent,
    _value: string,
    reason: string
  ): void => {
    if (reason === 'clear') {
      setSearchInput('');
      onChange(defaultValue as Record<string, string | number>);
      if (options) {
        setSearchResults(options);
      }
    }
  };

  const filteredOptions = searchResults?.filter(
    (result) =>
      !projectMembers?.includes(
        result.id as unknown as Record<string, string | number>
      )
  );

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          color: '#44474E',
          marginTop: '15px'
        }}
      >
        {label}
      </Typography>
      <Autocomplete
        id={id}
        sx={{
          width: {
            xs: '100%',
            sm: '100%'
          },
          height: '55px',
          marginTop: '15px',
          '.MuiAutocomplete-listbox': {
            height: '50px'
          }
        }}
        value={value}
        options={filteredOptions}
        getOptionLabel={(option) =>
          option[optionLabelKey] ? option[optionLabelKey].toString() : ''
        }
        onChange={(_event, newValue) => {
          if (newValue) {
            onChange(newValue);
          }
        }}
        onInputChange={handleInputChange}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            onChange={(event) => {
              handleSearchingName(event.target.value);
            }}
          />
        )}
        disabled={disabled}
        loading={loading}
      />
      <Typography
        sx={{
          color: 'red',
          fontSize: '10px'
        }}
      >
        {touched && errors}
      </Typography>
    </Box>
  );
};

export default AutoCompleteSearchComponent;
